import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/common/tabs'
import { BORROW_MARKET_PAGE_KEYS } from '@/constants/state'
import { useAccountContext } from '@/context/account'
import { useSearchState } from '@/hooks/useSearchState'
import type { BorrowMarketAssetData, BorrowMarketDetail } from '@/utils/internalAppSdk'
import { BorrowMarketDetailsStats } from './borrowMarketDetailsStats'
import { BorrowMarketInterestRateModel } from './borrowMarketInterestRateModel'
import { BorrowMarketParameters } from './borrowMarketParameters'
import { BorrowMarketPositionDetails } from './borrowMarketPositionDetails'

const BORROW_MARKET_TAB_VALUES = {
  overview: 'overview',
  position: 'position',
} as const

const TABS = [
  { key: BORROW_MARKET_TAB_VALUES.overview, label: 'Overview' },
  { key: BORROW_MARKET_TAB_VALUES.position, label: 'Position details' },
] as const

type TabKey = (typeof TABS)[number]['key']

interface Props {
  market: BorrowMarketDetail
  borrowAssetData: BorrowMarketAssetData
}

export const BorrowMarketContent = ({ market, borrowAssetData }: Props) => {
  const { account } = useAccountContext()
  const { searchState, updateSearchState } = useSearchState({})

  const rawTab = searchState.get(BORROW_MARKET_PAGE_KEYS.tab.key)
  const isPositionDisabled = !account
  const tabFromUrl = TABS.some((tab) => tab.key === rawTab) ? (rawTab as TabKey) : BORROW_MARKET_TAB_VALUES.overview
  const activeTab: TabKey =
    tabFromUrl === BORROW_MARKET_TAB_VALUES.position && isPositionDisabled
      ? BORROW_MARKET_TAB_VALUES.overview
      : tabFromUrl

  return (
    <Tabs
      value={activeTab}
      onValueChange={(value) => updateSearchState([BORROW_MARKET_PAGE_KEYS.tab.key, value])}
      className="overflow-hidden"
    >
      <div className="flex flex-col gap-4">
        <TabsList variant="line">
          <TabsTrigger variant="line" value={BORROW_MARKET_TAB_VALUES.overview}>
            Overview
          </TabsTrigger>
          <TabsTrigger variant="line" value={BORROW_MARKET_TAB_VALUES.position} disabled={isPositionDisabled}>
            Your position & activity
          </TabsTrigger>
        </TabsList>
        <TabsContent value={BORROW_MARKET_TAB_VALUES.overview}>
          <div className="flex flex-col gap-4">
            <BorrowMarketDetailsStats
              network={market.network.name}
              marketId={market.marketId}
              borrowAssetData={borrowAssetData}
            />
            <BorrowMarketParameters market={market} borrowAssetData={borrowAssetData} />
            <BorrowMarketInterestRateModel borrowAssetData={borrowAssetData} />
          </div>
        </TabsContent>
        <TabsContent value={BORROW_MARKET_TAB_VALUES.position}>
          {account && (
            <BorrowMarketPositionDetails address={account} market={market} borrowAssetData={borrowAssetData} />
          )}
        </TabsContent>
      </div>
    </Tabs>
  )
}
